import React, { useEffect, useState } from "react";
import { ref, onValue, remove, set, get } from "firebase/database";
import { database, firestore } from "../../../firebaseConfig";
import { doc, updateDoc } from "firebase/firestore";
import toast from "react-hot-toast";

const PendingRequests = ({ companyName }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snapshots, setSnapshots] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);

  // -------------------------------------------------------------
  // Load pending requests
  // -------------------------------------------------------------
  useEffect(() => {
    if (!companyName) return;

    const reqRef = ref(
      database,
      `Companies/${companyName}/FarmerAttachRequests`
    );

    const unsub = onValue(reqRef, (snap) => {
      const data = snap.val() || {};
      setRequests(Object.values(data));
      setLoading(false);
    });

    return () => unsub();
  }, [companyName]);

  // -------------------------------------------------------------
  // Fetch validated farmers who are NOT linked yet
  // -------------------------------------------------------------
  const fetchSnapshots = async () => {
    if (!companyName) return;

    const farmersRef = ref(
      database,
      `Companies/${companyName}/ValidatedFarmers`
    );

    const snap = await get(farmersRef);

    const arr = [];
    if (snap.exists()) {
      const data = snap.val();

      Object.entries(data).forEach(([id, f]) => {
        if (!f.LinkedFarmer || f.LinkedFarmer === "") {
          arr.push({ id, ...f });
        }
      });
    }

    setSnapshots(arr);
  };

  // -------------------------------------------------------------
  // Attach flow (step 1 → open snapshot selector)
  // -------------------------------------------------------------
  const handleAttach = async (req) => {
    setSelectedUid(req.uid);
    setSelectedRequest(req); // Store the full request object
    setSelectedSnapshotId(""); // Reset selection
    await fetchSnapshots();
  };

  // -------------------------------------------------------------
  // Final confirm attach logic
  // -------------------------------------------------------------
  const confirmAttach = async () => {
    if (!selectedSnapshotId) {
      toast.error("Please select a farmer profile to attach");
      return;
    }

    try {
      // Find snapshot object
      const snapObj = snapshots.find((s) => s.id === selectedSnapshotId);

      // 1️⃣ Update ValidatedFarmers to link this UID
      await set(
        ref(
          database,
          `Companies/${companyName}/ValidatedFarmers/${selectedSnapshotId}/LinkedFarmer`
        ),
        selectedUid
      );

      // 2️⃣ Update Firestore user — approved
      // Provide fullName from ValidatedFarmer's CloudName and update farmer side
      await updateDoc(doc(firestore, "users", selectedUid), {
        companyName,
        fullName: snapObj?.CloudName || selectedRequest?.fullName || "", // Use CloudName from validated farmer
        otherName: snapObj?.OtherName || "",
        pendingAttach: {
          companyName,
          status: "approved",
          decidedAt: Date.now(),
        },
      });

      // 3️⃣ Remove the request from RTDB
      await remove(
        ref(database, `Companies/${companyName}/FarmerAttachRequests/${selectedUid}`)
      );

      toast.success("Farmer successfully attached!");

      setSelectedUid(null);
      setSelectedSnapshotId("");
      setSnapshots([]);
      setSelectedRequest(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to attach farmer");
    }
  };

  // -------------------------------------------------------------
  // Reject request
  // -------------------------------------------------------------
  const rejectRequest = async (uid, company) => {
    try {
      // Remove real-time request
      await remove(
        ref(database, `Companies/${company}/FarmerAttachRequests/${uid}`)
      );

      // Update Firestore → declined
      await updateDoc(doc(firestore, "users", uid), {
        pendingAttach: {
          companyName: company,
          status: "declined",
          decidedAt: Date.now(),
        },
      });

      toast("Request declined");
    } catch (err) {
      console.error(err);
      toast.error("Failed to decline request");
    }
  };

  // -------------------------------------------------------------
  // UI
  // -------------------------------------------------------------
  if (loading) return <p>Loading requests...</p>;

  return (
    <div>
      <h2 className="text-xl font-semibold text-cyan-700 mb-4">
        📩 Pending Farmer Attach Requests
      </h2>

      {requests.length === 0 && (
        <p className="text-gray-500">No pending requests</p>
      )}

      {requests.map((req) => (
        <div
          key={req.uid}
          className="p-4 bg-white border rounded-lg shadow mb-3"
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="font-medium text-gray-800">{req.fullName}</p>
              <p className="text-gray-600 text-sm">{req.email}</p>
              {req.description && (
                <div className="mt-2 p-2 bg-cyan-50 rounded border-l-4 border-cyan-500">
                  <p className="text-xs font-semibold text-cyan-700 mb-1">Farmer Info:</p>
                  <p className="text-sm text-gray-700">{req.description}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 ml-4">
              <button
                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
                onClick={() => rejectRequest(req.uid, companyName)}
              >
                Decline
              </button>

              <button
                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition"
                onClick={() => handleAttach(req)}
              >
                Attach
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* SNAPSHOT SELECT MODAL */}
      {selectedUid && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-xl w-96">
            <h3 className="text-lg font-semibold mb-3">
              Select Farmer Snapshot
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              Choose which validated farmer profile to link with this request:
            </p>

            <select
              value={selectedSnapshotId}
              onChange={(e) => setSelectedSnapshotId(e.target.value)}
              className="w-full p-2 border rounded mb-4"
            >
              <option value="">Choose farmer...</option>

              {snapshots.map((snap) => (
                <option key={snap.id} value={snap.id}>
                  {snap.CloudName} — {snap.OtherName}
                </option>
              ))}
            </select>

            <div className="flex gap-2">
              <button
                className="flex-1 py-2 bg-gray-300 rounded hover:bg-gray-400 transition"
                onClick={() => {
                  setSelectedUid(null);
                  setSelectedSnapshotId("");
                  setSelectedRequest(null);
                }}
              >
                Cancel
              </button>

              <button
                className="flex-1 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={confirmAttach}
                disabled={!selectedSnapshotId}
              >
                Attach
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingRequests;
